import { createSelector } from "@reduxjs/toolkit";

import { FormId } from "../../../../components/enums";
import { RootState } from "../../../../store";
import { UIState } from "../../../../store/HOC/genericUI.slice";
import {
  generateAddress,
  generatePhone,
  mapPersonTypeToLabelProperty,
} from "../../../../utilities/forms";

import { ComponentId as CompanyComponentId } from "./CompanyData/enums";
import { ComponentId as ContactPersonComponentId } from "./ContactPersonData/enums";
import { ComponentId as GeneralComponentId } from "./GeneralData/enums";
import { ComponentId as PersonComponentId } from "./PersonData/enums";
import {
  OwnerGeneralFormInputs,
  OwnerPersonFormInputs,
  OwnerCompanyFormInputs,
  OwnerContactPersonFormInputs,
  FormDataInputs,
} from "./owner.slice";

export const selectOwnerGeneralData = (
  store: RootState
): OwnerGeneralFormInputs => store.ocProperty.owner.data[FormId.OWNER_GENERAL];

export const selectOwnerPersonFormData = (
  store: RootState
): OwnerPersonFormInputs =>
  store.ocProperty.owner.data[FormId.OWNER_PERSON_DATA];

export const selectOwnerCompanyFormData = (
  store: RootState
): OwnerCompanyFormInputs =>
  store.ocProperty.owner.data[FormId.OWNER_COMPANY_DATA];

export const selectOwnerContactPersonFormData = (
  store: RootState
): OwnerContactPersonFormInputs =>
  store.ocProperty.owner.data[FormId.OWNER_CONTACT_PERSON_DATA];

export const selectShouldDisplayPerson = createSelector(
  selectOwnerGeneralData,
  (generalData) => generalData[GeneralComponentId.TYPE] === "person"
);

export const selectShouldDisplayCompany = createSelector(
  selectOwnerGeneralData,
  (generalData) => generalData[GeneralComponentId.TYPE] === "company"
);

export const selectShouldDisplayContactPersonTypeForm = createSelector(
  selectOwnerGeneralData,
  (generalData) => generalData[GeneralComponentId.TYPE] !== ""
);

export const selectShouldDisplayContactPersonDetailsForm = createSelector(
  selectOwnerContactPersonFormData,
  (contactPersonData) =>
    contactPersonData[ContactPersonComponentId.TYPE] === "otherPerson"
);

const selectOwnerCompanyName = createSelector(
  selectOwnerCompanyFormData,
  (data) => ({
    name: "Nazwa firmy",
    value: data[CompanyComponentId.NAME],
  })
);

const selectOwnerCompanyRegon = createSelector(
  selectOwnerCompanyFormData,
  (data) => ({
    name: "REGON",
    value: data[CompanyComponentId.REGON],
  })
);

const selectOwnerCompanyTel = createSelector(
  selectOwnerCompanyFormData,
  (data) => ({
    name: "Tel.",
    value: generatePhone(data[CompanyComponentId.PHONE]),
  })
);

const selectOwnerCompanyEmail = createSelector(
  selectOwnerCompanyFormData,
  (data) => ({
    name: "Email",
    value: data[CompanyComponentId.EMAIL],
  })
);

const selectOwnerCompanyAddress = createSelector(
  selectOwnerCompanyFormData,
  (data) => ({
    name: "Ulica, kod pocztowy, miejscowość",
    value: generateAddress(data),
  })
);

const selectOwnerCompanyData = createSelector(
  selectOwnerCompanyName,
  selectOwnerCompanyRegon,
  selectOwnerCompanyTel,
  selectOwnerCompanyEmail,
  selectOwnerCompanyAddress,
  (name, regon, tel, email, address) => [name, regon, tel, email, address]
);

const selectOwnerPersonNameSurname = createSelector(
  selectOwnerPersonFormData,
  (data) => ({
    name: "Imię i nazwisko",
    value: [data[PersonComponentId.NAME], data[PersonComponentId.SURNAME]]
      .join(" ")
      .trim(),
  })
);

const selectOwnerPersonTel = createSelector(
  selectOwnerPersonFormData,
  (data) => ({
    name: "Tel.",
    value: generatePhone(data[PersonComponentId.PHONE]),
  })
);

const selectOwnerPersonEmail = createSelector(
  selectOwnerPersonFormData,
  (data) => ({
    name: "Email",
    value: data[PersonComponentId.EMAIL],
  })
);

const selectOwnerPersonPesel = createSelector(
  selectOwnerPersonFormData,
  (data) => ({
    name: "PESEL",
    value: data[PersonComponentId.PESEL],
  })
);

const selectOwnerPersonAddress = createSelector(
  selectOwnerPersonFormData,
  (data) => ({
    name: "Ulica, kod pocztowy, miejscowość",
    value: generateAddress(data),
  })
);

const selectOwnerPersonData = createSelector(
  selectOwnerPersonNameSurname,
  selectOwnerPersonTel,
  selectOwnerPersonEmail,
  selectOwnerPersonPesel,
  selectOwnerPersonAddress,
  (nameSurname, tel, email, pesel, address) => [
    nameSurname,
    tel,
    email,
    pesel,
    address,
  ]
);

const selectState = (state: RootState) => state;

const selectOwnerOwnerSummaryData = createSelector(
  selectShouldDisplayCompany,
  selectState,
  (isCompany: boolean, state: RootState) => [
    ...(isCompany
      ? selectOwnerCompanyData(state)
      : selectOwnerPersonData(state)),
  ]
);

const selectOwnerContactPersonType = createSelector(
  selectOwnerContactPersonFormData,
  (data) => ({
    name: "Kto jest osobą kontaktową",
    value: mapPersonTypeToLabelProperty[data.type] || "",
  })
);

const selectContactPersonNameSurname = createSelector(
  selectOwnerContactPersonFormData,
  (data) => ({
    name: "Imię i nazwisko",
    value: [
      data[ContactPersonComponentId.NAME],
      data[ContactPersonComponentId.SURNAME],
    ]
      .join(" ")
      .trim(),
  })
);

const selectContactPersonCompanyName = createSelector(
  selectOwnerContactPersonFormData,
  (data) => ({
    name: "Nazwa firmy",
    value: data[ContactPersonComponentId.COMPANY_NAME],
  })
);

const selectContactPersonTel = createSelector(
  selectOwnerContactPersonFormData,
  (data) => ({
    name: "Tel.",
    value: generatePhone(data[ContactPersonComponentId.PHONE]),
  })
);

const selectContactPersonEmail = createSelector(
  selectOwnerContactPersonFormData,
  (data) => ({
    name: "Email",
    value: data[ContactPersonComponentId.EMAIL],
  })
);

const selectOwnerContactPersonDetailsData = createSelector(
  selectContactPersonNameSurname,
  selectContactPersonCompanyName,
  selectContactPersonTel,
  selectContactPersonEmail,
  (nameSurname, companyName, tel, email) => [
    nameSurname,
    companyName,
    tel,
    email,
  ]
);

const selectOwnerContactPersonSummaryData = createSelector(
  selectOwnerContactPersonType,
  selectOwnerContactPersonDetailsData,
  (type, details) => [type, ...details]
);

export const selectSummaryData = createSelector(
  selectOwnerOwnerSummaryData,
  selectOwnerContactPersonSummaryData,
  (owner, contactPerson) => ({ owner, contactPerson })
);

export const selectFormData = (state: RootState): FormDataInputs =>
  state.ocProperty.owner.data;

export const selectUIData = (store: RootState): UIState =>
  store.ocProperty.owner.ui;

export const selectOwnerAvailableCities = (store: RootState) =>
  store.ocProperty.owner.ui.ownerAvailableCities || [];
